import { NavLink, Navigate, Outlet, useParams } from "react-router-dom";
import { AppHeader } from "../../shared/AppHeader";
import { useMe } from "../../shared/hooks/useMe";
import { Spinner } from "../../ui";
import {
  rememberProduct,
  sectionsOf,
  useAdminWorkspace,
} from "./useAdminWorkspace";

/** Sections that belong to no single product (docs/14 §1); the owner's level. */
const PLATFORM_LINKS = [
  ["/admin/platform", "Overview"],
  ["/admin/platform/products", "Products"],
  ["/admin/platform/staff", "Staff"],
  ["/admin/platform/audit", "Audit"],
  ["/admin/platform/security", "Security"],
] as const;

function navClass({ isActive }: { isActive: boolean }): string {
  return `rounded-md px-3 py-2 text-sm ${
    isActive
      ? "bg-brand-tint/50 font-medium text-brand-dark"
      : "text-slate-700 hover:bg-slate-100"
  }`;
}

/** The console: a workspace per product, plus the platform level (docs/14 §1). */
export function AdminLayout() {
  const me = useMe();
  const workspace = useAdminWorkspace();
  const { slug } = useParams();

  if (me.isLoading || workspace.isLoading) {
    return (
      <main className="p-8">
        <Spinner />
      </main>
    );
  }
  if (!me.data) return <Navigate to="/login?return_to=%2Fadmin" replace />;
  if (!workspace.isStaff) return <Navigate to="/account" replace />;

  const product = slug
    ? workspace.products.find((p) => p.slug === slug)
    : undefined;
  if (slug && !product) return <Navigate to="/admin" replace />;
  if (product) rememberProduct(product.slug);

  const role = product?.role ?? workspace.platformRole;
  const links = product
    ? sectionsOf(product)
    : PLATFORM_LINKS.map(([to, label]) => [to, label] as [string, string]);

  return (
    <>
      <AppHeader
        context={
          product
            ? { kind: "product", slug: product.slug, role: role ?? "support" }
            : { kind: "platform" }
        }
      />
      <div className="mx-auto flex max-w-6xl flex-col gap-6 p-4 md:flex-row">
        <nav
          className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-card md:w-52"
          aria-label="Staff dashboard"
        >
          <div className="flex flex-row gap-2 overflow-x-auto md:flex-col">
            {links.map(([to, label]) => (
              <NavLink
                key={to}
                to={to}
                end={
                  to === "/admin/platform" || to === `/admin/p/${product?.slug}`
                }
                className={navClass}
              >
                {label}
              </NavLink>
            ))}
          </div>
          <NavLink
            to="/account"
            className="rounded-md px-3 py-2 text-sm text-slate-500 hover:bg-slate-100"
          >
            My account
          </NavLink>
          <div className="px-3 py-2 text-xs text-slate-400">role: {role}</div>
        </nav>
        <section className="flex-1">
          <Outlet context={{ role, product: product?.slug ?? null }} />
        </section>
      </div>
    </>
  );
}
